import { config } from '../../config';
import { trace } from './trace';
import { routeStorage } from '../login/storage';
import { reportErrorToSentry } from './sentry';
import type { UserManager } from 'oidc-client-ts';

const saveCurrentRoute = () => {
    const initialRoute = `/${window.location.hash}`;

    routeStorage.saveRoute(initialRoute);

    trace('saving initial route', initialRoute);
};

const doSignIn = (userManager: UserManager, isAuthCodeRedirect: boolean) =>
    isAuthCodeRedirect ? userManager.signinCallback() : userManager.signinSilent();

const redirectIfStoredStateIsMissing = async (userManager: UserManager) => {
    const searchParams = new URLSearchParams(window.location.search);
    const state = searchParams.get('state');
    if (state !== null && (await userManager.settings.stateStore.get(state)) === null) {
        // Ensure that there is a state stored. In some cases, clearStaleState might remove the state from storage
        // which leads to issues when loading the page
        console.warn(`Signin error: No matching state found in storage. Redirecting to ${window.location.origin}`);

        routeStorage.discardRoute();

        // We want a real page reload, which will either:
        //  - perform a silent signin, which succeeds if the user already has a session from another login
        //  - trigger a new redirection that brings us back here with a valid state value.
        // We use replace here to override the redirect URL and prevent the back button to go
        // back to the redirect page.
        window.location.replace(window.location.origin);

        // The window.location.replace call runs in the background. We do not want
        // to continue with the rest of the authorization flow. This would just lead
        // to a thrown error from signinCallback(), which we would see in the monitoring.
        // Therefore, we block this promise to let the navigation happen.
        await new Promise<never>(() => {});
    }
};

export const attemptInitialSignIn = async (userManager: UserManager) => {
    const isAuthCodeRedirect = window.location.href.startsWith(config.login.redirectUri as string);

    await redirectIfStoredStateIsMissing(userManager);

    try {
        const user = await doSignIn(userManager, isAuthCodeRedirect);
        const initialRoute = routeStorage.getRoute();

        trace('initialRoute lookup', initialRoute);

        if (initialRoute && isAuthCodeRedirect && !redirectForDeprecatedLocation(initialRoute)) {
            trace(`Go to location "${initialRoute}"`);
            routeStorage.discardRoute();
            window.history.replaceState({}, '', initialRoute);
        } else if (isAuthCodeRedirect && !redirectForDeprecatedLocation(initialRoute)) {
            window.history.replaceState({}, '', window.location.origin);
        }

        routeStorage.discardRoute();
        return user;
    } catch (error) {
        trace('oidc.signinSilent failed, trying page redirect...', error);

        if (config.login.preventRedirect) {
            console.warn('[feature/login] redirect prevented due to config. Error was', error);
        } else if (isAuthCodeRedirect) {
            trace('oidc.signinSilent.error', 'redirect prevented due to suspicious signin error', error);
            routeStorage.discardRoute();
            reportErrorToSentry(error);
        } else {
            saveCurrentRoute();
            await userManager.signinRedirect();
        }

        trace('auth problem?', error);
        throw new Error('Need to sign in');
    }
};

const redirectForDeprecatedLocation = (initialRoute: string | null) => {
    if (initialRoute && initialRoute.indexOf('/#tags') >= 0) {
        const newRoute = '/';
        trace(`Deprecated url loaded. Redirecting to "${newRoute}"`);
        window.history.replaceState({}, '', newRoute);
        return true;
    }
    return false;
};
